import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Per-viewer fold (open/closed) state for the campaign builder's playlist
 * accordions — spec item D. Keyed per campaign + layer in localStorage
 * (`pp_cb_playlist_fold_${campaignId}_${layer}`), wrapped in try/catch so a
 * viewer with storage disabled/blocked still gets a working page (falls
 * back to in-memory-only state for that session). Default state for any
 * playlist not yet in the stored map is OPEN, per spec.
 */
function storageKey(campaignId, layer) {
  return `pp_cb_playlist_fold_${campaignId}_${layer}`;
}

function readState(campaignId, layer) {
  try {
    const raw = window.localStorage.getItem(storageKey(campaignId, layer));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeState(campaignId, layer, state) {
  try {
    window.localStorage.setItem(storageKey(campaignId, layer), JSON.stringify(state));
  } catch (e) {
    // Storage disabled/blocked (private mode, quota, etc.) — the page keeps
    // working off the in-memory React state for the rest of this session.
  }
}

export function usePlaylistFoldState(campaignId, layer, playlistIds) {
  const [state, setState] = useState(() => readState(campaignId, layer));

  useEffect(() => {
    setState(readState(campaignId, layer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, layer]);

  const persist = useCallback(
    (next) => {
      setState(next);
      writeState(campaignId, layer, next);
    },
    [campaignId, layer]
  );

  const isOpen = useCallback((playlistId) => state[playlistId] !== false, [state]);

  const toggle = useCallback(
    (playlistId) => {
      persist({ ...state, [playlistId]: !isOpen(playlistId) });
    },
    [state, isOpen, persist]
  );

  const idsKey = (playlistIds || []).join(',');
  const anyOpen = useMemo(
    () => (playlistIds || []).some((id) => isOpen(id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey, state]
  );

  const setAll = useCallback(
    (openValue) => {
      const next = { ...state };
      (playlistIds || []).forEach((id) => {
        next[id] = openValue;
      });
      persist(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey, state, persist]
  );

  return { isOpen, toggle, anyOpen, setAll };
}

export default usePlaylistFoldState;
